import routes from 'src/Resources/routes.json';

/**
 * Installation.product -> the URL prefix that product's installations live under.
 *
 * Every product family mounts its own route tree in App.tsx (product 0 and 3 and 4 on
 * InstallationTabs, 1 on SalidomoInstallationTabs, 2 and 5 on SodioHomeInstallationTabs), so an
 * installation id alone is not enough to build a link -- the prefix has to come from the product.
 *
 * This map used to be copy-pasted at every call site. Keep it here: a new product is one line in
 * one place, and a missed copy silently sends the user to the wrong product's list instead of
 * failing loudly.
 */
const PRODUCT_ROUTES: Record<number, string> = {
  0: routes.installations,
  1: routes.salidomo_installations,
  2: routes.sodiohome_installations,
  3: routes.sodistore_installations,
  4: routes.sodistoregrid_installations,
  5: routes.sodistorepro_installations
};

/**
 * The route prefix for a product, e.g. 2 -> "/sodiohome_installations/".
 *
 * An unknown product falls back to the Salimax prefix. ⚠ That fallback is a guess and it is WRONG
 * for any product added later: the link simply opens the wrong product's list, silently. Every
 * product in ProductType must be in the map above — the fallback exists so a bad `product` value
 * cannot produce a broken URL, not as a substitute for adding the line.
 */
export const productRoute = (product: number): string =>
  PRODUCT_ROUTES[product] ?? routes.installations;

/**
 * Full in-app path to one tab of one installation, e.g.
 *   installationUrl(2, 835, routes.installationTickets)
 *     -> "/sodiohome_installations/list/installation/835/installationTickets"
 *
 * `tab` is a value from routes.json (routes.live, routes.information, routes.log,
 * routes.installationTickets, ...). Note the tab a caller picks is not always reachable: when an
 * installation has dataCollectionEnabled === false only the tabs in `allowedWhenDisabled` render
 * (see SodiohomeInstallations/index.tsx), and the Tickets tab additionally depends on
 * PARTNER_TICKETS_ENABLED for non-admin users.
 */
export const installationUrl = (
  product: number,
  installationId: number | string,
  tab: string,
  // Which navigation the link belongs to. Links built from the tree must stay in the tree —
  // sending a tree click to the flat list drops the user out of the navigation they were using.
  segment: string = routes.list
): string =>
  productRoute(product) +
  segment +
  routes.installation +
  installationId +
  '/' +
  tab;
